import { THUMB_KANTE, THUMB_MAX_BYTES } from '@fh/shared/media'

/**
 * Vorschaubild und Dauer für Video und Ton — auf dem Gerät, wie bei Bildern.
 *
 * ## Warum ein echter Einzelbild-Auszug und kein Symbol
 *
 * In Zone A kann der Server keine Vorschau erzeugen; er sieht ein
 * verschlüsseltes Paket. Bliebe es bei einem Filmklappen-Symbol, sähen zehn
 * Videos in einem Verlauf alle gleich aus — man müsste jedes einzeln laden, um
 * zu wissen, welches das vom Geburtstag ist. Ein Bild aus dem Film beantwortet
 * das ohne Download.
 *
 * ## Warum nicht der erste Frame
 *
 * Der erste Frame eines Handyvideos ist oft schwarz oder verwackelt — die
 * Kamera belichtet noch. Genommen wird deshalb ein Bild aus dem ersten Drittel,
 * höchstens aber nach einer Sekunde: weit genug hinein für ein brauchbares
 * Bild, nah genug am Anfang, dass der Browser nicht durch die halbe Datei
 * suchen muss.
 *
 * ## Was hier NICHT passiert
 *
 * Anders als bei Fotos wird die Datei **nicht neu kodiert**. Das hieße, ein
 * Video im Browser umzuwandeln — dafür bräuchte es eine schwergewichtige
 * Abhängigkeit, und die Bildqualität bezahlte man mit ihr. Folge: Ortsangaben
 * im Video bleiben drin. Das ist keine Nachlässigkeit, sondern der Grund,
 * warum `anhang.ts` für solche Formate ausdrücklich nachfragt.
 */

/** Nach so langer Zeit ohne Bild wird aufgegeben. */
const FRIST_MS = 8000

export interface MedienVorschau {
  vorschau: Blob | null
  breite: number
  hoehe: number
  /** Sekunden, auf ganze gerundet. 0, wenn der Browser es nicht sagt. */
  dauer: number
}

export function kannVorschau(): boolean {
  return typeof OffscreenCanvas !== 'undefined' && typeof document !== 'undefined'
}

/**
 * Liest Dauer und — bei Video — ein Vorschaubild.
 *
 * Scheitert das, ist das kein Fehler: `vorschau: null` heißt „ohne Bild", und
 * der Anhang geht trotzdem raus. Ein Video, das man nicht schicken kann, weil
 * der Browser kein Vorschaubild hinbekommt, wäre die schlechtere Antwort.
 */
export async function medienVorschau(datei: File): Promise<MedienVorschau> {
  const ton = datei.type.startsWith('audio/')
  const url = URL.createObjectURL(datei)
  const el = document.createElement(ton ? 'audio' : 'video') as HTMLVideoElement
  el.preload = 'metadata'
  el.muted = true
  // Ohne das lädt Safari auf dem Telefon gar nichts, solange nichts sichtbar ist.
  el.playsInline = true

  try {
    const dauer = await warteAuf(el, 'loadedmetadata', () => { el.src = url })
      .then(() => Number.isFinite(el.duration) ? Math.round(el.duration) : 0)
      .catch(() => 0)

    if (ton || dauer === 0 && !el.videoWidth) {
      return { vorschau: null, breite: 0, hoehe: 0, dauer }
    }

    const stelle = Math.min(1, Math.max(0, el.duration / 3))
    await warteAuf(el, 'seeked', () => { el.currentTime = stelle })

    const breite = el.videoWidth
    const hoehe = el.videoHeight
    if (!breite || !hoehe) return { vorschau: null, breite: 0, hoehe: 0, dauer }

    const faktor = Math.min(1, THUMB_KANTE / Math.max(breite, hoehe))
    const canvas = new OffscreenCanvas(
      Math.max(1, Math.round(breite * faktor)), Math.max(1, Math.round(hoehe * faktor)))
    const ctx = canvas.getContext('2d')
    if (!ctx) return { vorschau: null, breite, hoehe, dauer }
    ctx.drawImage(el, 0, 0, canvas.width, canvas.height)

    let vorschau = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.7 })
    if (vorschau.size > THUMB_MAX_BYTES) {
      vorschau = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.5 })
    }
    return { vorschau, breite, hoehe, dauer }
  } catch {
    // Ein Codec, den dieser Browser nicht kann; ein Container, den er nicht
    // durchsucht. Beides heißt „kein Bild", nicht „kein Versand".
    return { vorschau: null, breite: 0, hoehe: 0, dauer: 0 }
  } finally {
    el.removeAttribute('src')
    el.load()
    URL.revokeObjectURL(url)
  }
}

/**
 * Wartet auf ein Medien-Ereignis — mit Frist.
 *
 * Ohne die Frist bliebe das Versprechen bei einem Codec, den der Browser nicht
 * kann, für immer offen: kein `seeked`, kein `error`, und der Anhang-Knopf
 * dreht sich bis zum Neuladen der Seite.
 */
function warteAuf(el: HTMLMediaElement, ereignis: string, ausloesen: () => void): Promise<void> {
  return new Promise<void>((ok, fehler) => {
    const uhr = setTimeout(() => { aufraeumen(); fehler(new Error('zu lange')) }, FRIST_MS)
    const fertig = () => { aufraeumen(); ok() }
    const kaputt = () => { aufraeumen(); fehler(new Error('Medienfehler')) }
    function aufraeumen() {
      clearTimeout(uhr)
      el.removeEventListener(ereignis, fertig)
      el.removeEventListener('error', kaputt)
    }
    el.addEventListener(ereignis, fertig, { once: true })
    el.addEventListener('error', kaputt, { once: true })
    ausloesen()
  })
}
